import { useState, useCallback, useRef } from 'react';

/**
 * 错误类型
 */
export enum ScriptErrorType {
  /** 网络错误 */
  NETWORK = 'network',
  /** 生成错误 */
  GENERATION = 'generation',
  /** 验证错误 */
  VALIDATION = 'validation',
  /** 保存错误 */
  SAVE = 'save',
  /** 未知错误 */
  UNKNOWN = 'unknown',
}

/**
 * 错误信息
 */
export interface ScriptError {
  type: ScriptErrorType;
  message: string;
  details?: string;
  retryable: boolean;
  timestamp: Date;
}

/**
 * 重试配置
 */
interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
};

/**
 * 脚本错误处理 Hook
 * 
 * 提供统一的错误处理和自动重试机制
 * 
 * Requirements: 12.1, 12.2, 12.3, 12.6
 */
export function useScriptErrorHandler(config: Partial<RetryConfig> = {}) {
  const [error, setError] = useState<ScriptError | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCountRef = useRef(0);
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };

  /**
   * 解析错误类型
   */
  const parseErrorType = useCallback((err: any): ScriptErrorType => {
    if (!err) return ScriptErrorType.UNKNOWN;

    // 网络错误
    if (err.name === 'NetworkError' || err.message?.includes('network') || err.code === 'ECONNREFUSED') {
      return ScriptErrorType.NETWORK;
    }

    // 超时错误
    if (err.name === 'TimeoutError' || err.code === 'ETIMEDOUT') {
      return ScriptErrorType.NETWORK;
    }

    // HTTP 错误
    if (err.response) {
      const status = err.response.status;
      if (status >= 500) return ScriptErrorType.NETWORK;
      if (status === 400) return ScriptErrorType.VALIDATION;
      if (status === 422) return ScriptErrorType.VALIDATION;
    }

    // 生成相关错误
    if (err.message?.includes('generation') || err.message?.includes('生成')) {
      return ScriptErrorType.GENERATION;
    }

    // 保存相关错误
    if (err.message?.includes('save') || err.message?.includes('保存')) {
      return ScriptErrorType.SAVE;
    }

    return ScriptErrorType.UNKNOWN;
  }, []);

  /**
   * 判断错误是否可重试
   */
  const isRetryable = useCallback((errorType: ScriptErrorType): boolean => {
    return [
      ScriptErrorType.NETWORK,
      ScriptErrorType.GENERATION,
      ScriptErrorType.SAVE,
    ].includes(errorType);
  }, []);

  /**
   * 处理错误
   */
  const handleError = useCallback((err: any): ScriptError => {
    const type = parseErrorType(err);
    const scriptError: ScriptError = {
      type,
      message: err.message || '发生未知错误',
      details: err.response?.data?.message || err.stack,
      retryable: isRetryable(type),
      timestamp: new Date(),
    };

    setError(scriptError);
    return scriptError;
  }, [parseErrorType, isRetryable]);

  /**
   * 清除错误
   */
  const clearError = useCallback(() => {
    setError(null);
    retryCountRef.current = 0;
  }, []);

  /**
   * 计算重试延迟（指数退避）
   */
  const calculateDelay = useCallback((retryCount: number): number => {
    const delay = retryConfig.baseDelay * Math.pow(2, retryCount);
    return Math.min(delay, retryConfig.maxDelay);
  }, [retryConfig.baseDelay, retryConfig.maxDelay]);

  /**
   * 带自动重试的执行函数
   */
  const executeWithRetry = useCallback(async <T>(
    fn: () => Promise<T>,
    options?: {
      onRetry?: (retryCount: number, error: ScriptError) => void;
      shouldRetry?: (error: ScriptError) => boolean;
    }
  ): Promise<T> => {
    retryCountRef.current = 0;
    clearError();

    const execute = async (): Promise<T> => {
      try {
        return await fn();
      } catch (err: any) {
        const scriptError = handleError(err);

        // 检查是否应该重试
        const shouldRetry = options?.shouldRetry?.(scriptError) ?? scriptError.retryable;

        if (shouldRetry && retryCountRef.current < retryConfig.maxRetries) {
          retryCountRef.current++;
          setIsRetrying(true);

          // 通知重试
          options?.onRetry?.(retryCountRef.current, scriptError);

          // 等待延迟
          const delay = calculateDelay(retryCountRef.current);
          await new Promise(resolve => setTimeout(resolve, delay));

          // 重试
          return execute();
        }

        setIsRetrying(false);
        throw err;
      }
    };

    try {
      const result = await execute();
      setIsRetrying(false);
      return result;
    } catch (err) {
      setIsRetrying(false);
      throw err;
    }
  }, [clearError, handleError, calculateDelay, retryConfig.maxRetries]);

  /**
   * 手动重试
   */
  const retry = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    if (!error?.retryable) {
      throw new Error('当前错误不可重试');
    }

    retryCountRef.current = 0;
    return executeWithRetry(fn);
  }, [error, executeWithRetry]);

  /**
   * 获取用户友好的错误消息
   */
  const getErrorMessage = useCallback((scriptError?: ScriptError | null): string => {
    const err = scriptError || error;
    if (!err) return '';

    switch (err.type) {
      case ScriptErrorType.NETWORK:
        return '网络连接失败，请检查网络后重试';
      case ScriptErrorType.GENERATION:
        return '生成失败，请稍后重试';
      case ScriptErrorType.VALIDATION:
        return err.message || '输入数据验证失败';
      case ScriptErrorType.SAVE:
        return '保存失败，请稍后重试';
      default:
        return err.message || '发生未知错误';
    }
  }, [error]);

  return {
    // 状态
    error,
    isRetrying,
    retryCount: retryCountRef.current,

    // 方法
    handleError,
    clearError,
    executeWithRetry,
    retry,
    getErrorMessage,

    // 工具
    parseErrorType,
    isRetryable,
  };
}

export type UseScriptErrorHandlerReturn = ReturnType<typeof useScriptErrorHandler>;
